import React, { useEffect } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useGetWhiteLabelCompanyQuery } from "../../../redux/admin/whiteLabelApi";
import { toast } from "react-hot-toast";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

function InfoItem({ label, value }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Stack>
  );
}

export default function WhiteLabelCompanyDetailsDialog({
  open,
  companyId,
  onClose,
}) {
  const theme = useTheme();
  const {
    data: company,
    isFetching,
    error,
  } = useGetWhiteLabelCompanyQuery(companyId, {
    skip: !open || !companyId,
  });

  useEffect(() => {
    if (!error) return;
    toast.error(error?.data?.msg || "Failed to load company details.");
  }, [error]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundImage: "none",
          bgcolor: alpha(theme.palette.background.paper, 0.98),
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          Company Details
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Review the saved company profile from the existing White Label backend.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {isFetching ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={{ minHeight: 280 }}
          >
            <CircularProgress size={28} />
            <Typography color="text.secondary">
              Loading company details...
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">
            {error?.data?.msg || "Failed to load company details."}
          </Alert>
        ) : company ? (
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {company.companyName}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  {company.companyCode}
                </Typography>
              </Box>
              <Chip
                label={String(company.status || "inactive").toUpperCase()}
                color={company.status === "active" ? "success" : "default"}
                variant={company.status === "active" ? "filled" : "outlined"}
                sx={{ fontWeight: 800 }}
              />
            </Stack>

            <Divider />

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Primary Domain" value={company?.domains?.primary} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Support Email" value={company?.support?.email} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Website URL" value={company?.website?.websiteUrl} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Android Build Status" value={company?.buildCenter?.androidStatus} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Pending Builds" value={String(company?.buildCenter?.pendingBuilds ?? 0)} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Created At" value={formatDate(company?.createdAt)} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Updated At" value={formatDate(company?.updatedAt)} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Created By" value={company?.createdBy?.username || company?.createdBy?.email} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoItem label="Updated By" value={company?.updatedBy?.username || company?.updatedBy?.email} />
              </Grid>
            </Grid>

            <Divider />

            <InfoItem label="Description" value={company?.description} />
          </Stack>
        ) : (
          <Alert severity="info">Company details are not available.</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
